import { HttpClient } from '../api/HttpClient';
import { API_ENDPOINTS } from '../api/ApiConfig';
import type {
  SplashScreen,
  SplashScreenCreateRequest,
  SplashScreenUpdateRequest,
  SplashScreenQueryParams,
  SplashScreensData,
} from '../../types/api/splash-screen';

/**
 * SplashScreenService
 *
 * Handles:
 * - Splash screen CRUD operations (admin)
 * - Create with image upload (multipart) or imageUrl (JSON)
 * - Update with optional image (multipart) or JSON only
 */
export class SplashScreenService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = HttpClient.getInstance();
  }

  /**
   * Create splash screen with image upload (multipart/form-data)
   * Parts: data (JSON), image (File)
   */
  async createWithImage(
    payload: SplashScreenCreateRequest,
    imageFile: File
  ): Promise<SplashScreen> {
    const formData = new FormData();

    const jsonBlob = new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    });
    formData.append('data', jsonBlob, 'data.json');
    formData.append('image', imageFile, imageFile.name);

    const response = await this.httpClient.request<SplashScreen>({
      method: 'POST',
      url: API_ENDPOINTS.SPLASH_SCREENS.ADMIN_CREATE_MULTIPART,
      data: formData,
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to create splash screen');
    }

    return response.data;
  }

  /**
   * Create splash screen using JSON-only endpoint (imageUrl required)
   */
  async createWithJson(payload: SplashScreenCreateRequest): Promise<SplashScreen> {
    const response = await this.httpClient.post<SplashScreen>(
      API_ENDPOINTS.SPLASH_SCREENS.ADMIN_CREATE_JSON,
      payload
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to create splash screen');
    }

    return response.data;
  }

  /**
   * Update splash screen with optional new image (multipart/form-data)
   */
  async updateWithImage(
    splashId: string,
    payload: SplashScreenUpdateRequest,
    imageFile?: File
  ): Promise<SplashScreen> {
    const formData = new FormData();

    const jsonBlob = new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    });
    formData.append('data', jsonBlob, 'data.json');

    if (imageFile) {
      formData.append('image', imageFile, imageFile.name);
    }

    const response = await this.httpClient.request<SplashScreen>({
      method: 'PUT',
      url: API_ENDPOINTS.SPLASH_SCREENS.ADMIN_UPDATE_MULTIPART(splashId),
      data: formData,
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update splash screen');
    }

    return response.data;
  }

  /**
   * Update splash screen using JSON-only endpoint (no image change)
   */
  async updateWithJson(
    splashId: string,
    payload: SplashScreenUpdateRequest
  ): Promise<SplashScreen> {
    const response = await this.httpClient.put<SplashScreen>(
      API_ENDPOINTS.SPLASH_SCREENS.ADMIN_UPDATE_JSON(splashId),
      payload
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to update splash screen');
    }

    return response.data;
  }

  /**
   * Get splash screen by ID (admin)
   */
  async getById(splashId: string): Promise<SplashScreen> {
    const response = await this.httpClient.get<SplashScreen>(
      API_ENDPOINTS.SPLASH_SCREENS.ADMIN_DETAIL(splashId)
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch splash screen');
    }

    return response.data;
  }

  /**
   * List splash screens for admin with pagination & filters
   */
  async list(params: SplashScreenQueryParams = {}): Promise<SplashScreensData> {
    const queryParams = new URLSearchParams();

    if (params.page !== undefined) queryParams.append('page', String(params.page));
    if (params.size !== undefined) queryParams.append('size', String(params.size));
    if (params.isActive !== undefined) queryParams.append('isActive', String(params.isActive));
    if (params.effectiveFrom) queryParams.append('effectiveFrom', params.effectiveFrom);
    if (params.effectiveTo) queryParams.append('effectiveTo', params.effectiveTo);
    if (params.targetPlatform) queryParams.append('targetPlatform', params.targetPlatform);
    if (params.language) queryParams.append('language', params.language);

    const url = `${API_ENDPOINTS.SPLASH_SCREENS.ADMIN_LIST}${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const response = await this.httpClient.get<SplashScreensData>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch splash screens');
    }

    return response.data;
  }

  /**
   * Delete splash screen (admin)
   */
  async delete(splashId: string): Promise<void> {
    const response = await this.httpClient.delete(
      API_ENDPOINTS.SPLASH_SCREENS.ADMIN_DELETE(splashId)
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete splash screen');
    }
  }
}
